/**
 * useCampaignSection Hook
 * Generic hook that delegates to strategy-specific implementations
 * Follows Open/Closed Principle - open for extension, closed for modification
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useViewMode, setViewMode, setSelectedSection } from 'store/app/appStore';
import { useOpenType } from 'core/providers/OpenTypeContext';
import { useGameSounds } from 'shared/hooks';
import { LOBBY_SECTION_NAME, MULTI_VIEW_MODE, SINGLE_VIEW_MODE } from '../../../../../constants';
import type ISectionSettings from '../../../../../types/models/ISectionSettings';

/** Common utilities shared across all campaign section strategies */
export const useCampaignSectionBase = <TData extends ISectionSettings>(data: Pick<TData, 'name'> & Partial<TData>, isActive: boolean) => {
  const { t } = useTranslation();
  const viewMode = useViewMode();
  const { openType } = useOpenType();
  const { playSelectTabSound } = useGameSounds();

  /** Standard view toggle handler */
  const handleViewToggle = useCallback(() => {
    if (viewMode === SINGLE_VIEW_MODE && isActive) {
      setSelectedSection(LOBBY_SECTION_NAME);
      setViewMode(MULTI_VIEW_MODE);
    } else {
      playSelectTabSound();
      setSelectedSection(data.name);
      setViewMode(SINGLE_VIEW_MODE);
    }
  }, [viewMode, isActive, data.name, setSelectedSection, setViewMode, playSelectTabSound]);

  /** Navigate back to lobby */
  const handleNavigateToLobby = useCallback(() => {
    setSelectedSection(LOBBY_SECTION_NAME);
    setViewMode(MULTI_VIEW_MODE);
  }, [setSelectedSection, setViewMode]);

  /** Create container props for CSS data attributes */
  const createContainerProps = useCallback(
    (additionalProps?: Record<string, string>) => ({
      'data-view': viewMode,
      'data-active': isActive ? 'true' : 'false',
      'data-section': data.name,
      'data-campaign-section': 'true',
      'data-open-type': openType,
      ...additionalProps,
    }),
    [viewMode, isActive, data.name, openType]
  );

  return {
    t,
    viewMode: viewMode as 'single' | 'multi',
    isInSingleView: viewMode === SINGLE_VIEW_MODE,
    isInMultiView: viewMode === MULTI_VIEW_MODE,
    handleViewToggle,
    handleNavigateToLobby,
    createContainerProps,
    sectionName: data.name,
    isActive,
  };
};
